/** A project's own swimlanes are free-form names now — see `PhaseDef` in `./phases` —
 *  so this is any string, not a closed set. Kept as its own alias for readability at
 *  call sites that predate that change. */
export type Phase = string;

/** Phase keys as they were once stored, mapped to their current spelling. */
export const PHASE_ALIASES: Record<string, Phase> = { Commisioning: "Commissioning" };

export type TaskType = "activity" | "milestone";

/**
 * Per-activity override of the project's global weekend-work setting. "auto" (the
 * default, and what a record saved before this field existed reads as) follows whatever
 * the project's own toggle says; "never"/"always" pin this one activity's own weekend
 * eligibility regardless of it — e.g. a noise-restricted activity that must never run on
 * a weekend even when the rest of the plan does, or an urgent one that's allowed to even
 * when the rest of the plan isn't.
 */
export type WeekendMode = "auto" | "never" | "always";

/** Any name is allowed — `SUBCONTRACTOR_OPTIONS` is just the seeded default list, not a closed set. */
export type Subcontractor = string;
export type ResponsibleParty = "" | "RWG" | "ABB";

export interface WbsNode {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
}

export interface Task {
  id: string;
  /** Stable work-package reference; undefined only in legacy records. */
  wbsId?: string | null;
  name: string;
  phase: Phase;
  planDay: string;
  baseline: number;
  /**
   * Optimistic/most-likely/pessimistic duration estimates. Their unit depends on
   * whether this activity itself runs a double shift (see `doubleShift` below): plain
   * calendar days when it doesn't (the default, and unchanged from before shifts
   * existed), or shift-units when it does, where 2 shift-units = 1 calendar day. The
   * scheduler (`cpm.ts`'s `taskShiftsPerDay`) converts to calendar days at the point it
   * computes ES/EF, so a single-team activity's numbers keep meaning exactly what they
   * always did even on a project that otherwise runs two shifts.
   */
  o: number;
  m: number;
  p: number;
  risk: string;
  type: TaskType;
  preds: string[];
  /** "Start no earlier than" day — set by dragging on the Lean board, or typed in the edit modal. */
  pinnedDay: number | null;
  /** Which shift column this task's card sits in on the Lean board when it's worked by
   *  a single team (see `doubleShift`) — settable by dragging the card, not from the
   *  edit modal. Undefined only on records saved before this field existed, or on a
   *  single-shift project — callers treat that the same as `null` (defaults to shift 1).
   *  Ignored once `doubleShift` is on: the card then sits in both shift columns. */
  pinnedShift?: 1 | 2 | null;
  /**
   * Whether this activity is worked by two teams at once, one per shift, on a project
   * that runs two shifts a day — as opposed to a single team that only works shift 1
   * (the default, `false`/undefined, meaning "single shift" even when the rest of the
   * project runs two). Set from the edit modal's "Shift 1" / "Shift 1 + 2" choice, and
   * has no effect at all on a project that doesn't run two shifts (see `twoShifts` on
   * `ProjectRecord`). Doubles this activity's own throughput: its computed duration is
   * halved (see `taskShiftsPerDay` in `cpm.ts`), and its card is drawn in both shift
   * columns on the Lean board instead of just one.
   */
  doubleShift?: boolean;
  /** This activity's own override of the project's weekend-work setting. Undefined only
   *  on records saved before this field existed — callers treat that the same as "auto". */
  weekendWork?: WeekendMode;
  subcontractors: Subcontractor[];
  responsibleParty: ResponsibleParty;
  requiredEquipment: string;
  requiredPersonnel: string;
  /**
   * Project days on which this task's work actually got done — a historical record,
   * not a plan. Rescheduling never moves these. Each entry consumes one day of the
   * planned duration, so `duration - doneDays.length` is the work still to do.
   */
  doneDays: number[];
  /** Work can't proceed right now. Doesn't change the schedule on its own — unfinished
   *  work already slides to today — it records *why* it keeps sliding. */
  blocked: boolean;
  blockedReason: string;
  /** Project day the block was raised, so a summary can say how long it's been standing.
   *  Null when it was raised with no calendar set, or before this was recorded. */
  blockedSince: number | null;
  /**
   * Explicit override of this task's actual start/finish project day, for cases the
   * day-by-day board can't cover — a P6-imported activity with no `doneDays` history,
   * or a manual correction. Undefined/null (the default, and every record saved before
   * these fields existed) means "derive it from `doneDays`" — see `effectiveActualStart`/
   * `effectiveActualFinish` in `cpm.ts`, which is what most tasks should rely on.
   */
  actualStart?: number | null;
  actualFinish?: number | null;
  /**
   * Site status this activity depends on to run safely — e.g. main trafo power must be
   * off during E-room work. Purely informational: checked against the linked StatusRow's
   * value for the day(s) this task is scheduled to warn of a mismatch, never changes the
   * row itself. `rowId` references `StatusRow.id`; a row that's since been removed is
   * simply not shown.
   */
  requiredStatus: { rowId: string; value: boolean }[];
}

export interface StatusRow {
  id: string;
  label: string;
  onLabel: string;
  offLabel: string;
  /**
   * Which value reads as "nothing to do here" — shown in a quiet/good color; any other
   * explicit value calls for attention. Main trafo power is normally On, so `true`; boom
   * position has no expected direction — what matters is whether it's constrained at
   * all — so `null`, meaning "unset" itself is the good state and either Up or Down
   * calls for attention.
   */
  normal: boolean | null;
  /**
   * values[day] — true = onLabel, false = offLabel, null/missing = not yet decided.
   * These are *planned* states, so "nobody has said" is genuinely different from
   * "planned off", and a wall chart people act on shouldn't render them alike.
   */
  values: (boolean | null)[];
}

export type StatusValue = boolean | null;

export type RiskLevel = "low" | "medium" | "high";
export type RiskStatus = "Needs input" | "Open" | "Mitigated" | "Monitoring" | "Closed";
export type ProbabilityImpact = "" | "Low" | "Medium" | "High";

export interface RiskEntry {
  id: string;
  desc: string;
  probability: ProbabilityImpact;
  impact: ProbabilityImpact;
  owner: string;
  mitigation: string;
  status: RiskStatus;
}

export type Role = "viewer" | "editor" | "admin";

export interface UserRecord {
  id: string;
  email: string;
  role: Role;
  name: string;
  avatar: string;
}

export interface AuthState {
  token: string;
  record: UserRecord;
}

export interface CPMError {
  ok: false;
  error: string;
}

export interface CPMResult {
  ok: true;
  topo: string[];
  succ: Record<string, string[]>;
  ES: Record<string, number>;
  EF: Record<string, number>;
  LS: Record<string, number>;
  LF: Record<string, number>;
  /**
   * Zero slack in the *remaining* plan. Finished work is never critical — it can no
   * longer move the end date — so a completed task drops off the critical path even
   * though it was on it while it ran.
   */
  critical: Record<string, boolean>;
  /** Every planned day has been marked done. ES/EF for these are the days it actually ran. */
  complete: Record<string, boolean>;
  /** Planned duration less the days already completed — what the schedule still has to place. */
  remaining: Record<string, number>;
  projectEnd: number;
}

export type CPMOutcome = CPMResult | CPMError;

export type DurationKey = "o" | "m" | "p";

export interface MonteCarloResult {
  samples: number[];
  p10: number;
  p50: number;
  p90: number;
  mean: number;
  iterations: number;
  /**
   * Per-task criticality index: the fraction of iterations (0-1) in which that task
   * came out of the simulated backward pass with zero slack — i.e. how often normal
   * duration uncertainty alone is enough to put it on the critical path, whether or
   * not it sits there in the deterministic (M-duration) schedule.
   */
  criticalityIndex: Record<string, number>;
}
